import { type ComponentProps } from 'react';

import { cn } from '@/util/utils';

type PageProps = ComponentProps<'div'> & {
    /**
     * The surface hierarchy rule: main pages sit on the tinted canvas so cards float; subpages
     * you navigate into sit on white and their cards use a header band instead. The
     * background tells you where you are.
     */
    surface?: 'main' | 'subpage';
};

/**
 * A screen inside the shell: header first, then a PageBody. Sets the surface. From lg the header
 * stays and the body scrolls under it; below lg the whole page scrolls with the document, so the
 * header goes up with the content (see AppShell).
 */
function Page({ surface = 'main', className, ...props }: PageProps) {
    return (
        <div
            data-slot="page"
            data-surface={surface}
            className={cn(
                'flex flex-1 flex-col lg:min-h-0 lg:overflow-hidden',
                surface === 'subpage' ? 'bg-card' : 'bg-background',
                className,
            )}
            {...props}
        />
    );
}

/**
 * The padded column under the header: 24px around, 20px between blocks. From lg it is what
 * scrolls, and it positions what is placed absolutely inside it: a visually hidden label with no
 * positioned ancestor would otherwise sit outside every clip and make the document scroll too.
 */
function PageBody({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot="page-body"
            className={cn(
                'flex flex-1 flex-col gap-(--layout-row-gap) p-4 sm:p-(--layout-body-padding) lg:relative lg:min-h-0 lg:[scrollbar-gutter:stable] lg:overflow-y-auto [&>*]:shrink-0',
                className,
            )}
            {...props}
        />
    );
}

export { Page, PageBody, type PageProps };
